import { useCallback, useRef } from 'react';

/**
 * Returns event handlers that fire `onLongPress` after the pointer has been
 * held for `delay` ms without moving more than `moveThreshold` pixels.
 * Works on both mouse and touch.
 */
export function useLongPress(onLongPress: () => void, delay = 600, moveThreshold = 10) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);

  const start = useCallback((x: number, y: number) => {
    startPos.current = { x, y };
    timer.current = setTimeout(() => {
      onLongPress();
      timer.current = null;
    }, delay);
  }, [onLongPress, delay]);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    startPos.current = null;
  }, []);

  const checkMove = useCallback((x: number, y: number) => {
    if (!startPos.current) return;
    const dx = Math.abs(x - startPos.current.x);
    const dy = Math.abs(y - startPos.current.y);
    if (dx > moveThreshold || dy > moveThreshold) cancel();
  }, [cancel, moveThreshold]);

  return {
    onMouseDown:  (e: React.MouseEvent) => start(e.clientX, e.clientY),
    onMouseUp:    cancel,
    onMouseLeave: cancel,
    onMouseMove:  (e: React.MouseEvent) => checkMove(e.clientX, e.clientY),
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (t) start(t.clientX, t.clientY);
    },
    onTouchEnd:   cancel,
    onTouchMove:  (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (t) checkMove(t.clientX, t.clientY);
    },
  };
}
